import {
  draggable,
  dropTargetForElements,
} from '@atlaskit/pragmatic-drag-and-drop/adapter/element-adapter';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/utils/combine';
import { attachClosestEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge/attach-closest-edge';
import { extractClosestEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge/extract-closest-edge';
import type { Edge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/types';
import { type CSSProperties, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router';

import { lookProps, lookSurface } from '@/entities/appearance';
import { useWorkspaces } from '@/entities/workspace';
import { BoardLabelsDialog } from '@/features/board-label-editor';
import { type Board, boardColor, LabelStrip, usePrefetchBoard } from '@/entities/board';
import { originStateFrom } from '@/shared/lib/origin';
import {
  CardActions,
  EmptyState,
  Icons,
  KindIcon,
  MenuItem,
  MenuList,
  MenuRow,
  MetaItem,
  MoveMenuItems,
  Popover,
  ResourceMeta,
} from '@/shared/ui';

import { BoardCardAppearanceDialog } from './BoardCardAppearanceDialog';
import styles from './BoardManage.module.css';
import { boardTileData, isBoardTile } from './dnd';
import { FavoriteButton } from './FavoriteButton';
import { splitFavorites } from './sort';

/** Where the tile may be moved to (the workspace page, manual order, no filters only). */
export interface TileOrder {
  previous: Board | undefined;
  next: Board | undefined;
  pending: boolean;
  onMove: (anchor: { after_id: string } | { before_id: string }) => void;
  /**
   * Drag and drop: `sourceId` (any board of this workspace) was dropped beside this tile, before
   * it or after it. Absent when dragging is not available (a narrowed list, another sort).
   */
  onDropBeside?: (sourceId: string, anchor: { after_id: string } | { before_id: string }) => void;
}

function TileMenu({
  board,
  order,
  onMoveToSection,
}: {
  board: Board;
  order: TileOrder | undefined;
  onMoveToSection: (() => void) | undefined;
}) {
  const { t } = useTranslation();
  const [styling, setStyling] = useState(false);
  const [labelling, setLabelling] = useState(false);
  const workspaces = useWorkspaces();
  const inTeam =
    workspaces.data?.find((w) => w.id === board.workspace_id)?.workspace_type === 'team';
  const { previous, next, pending, onMove } = order ?? {
    previous: undefined,
    next: undefined,
    pending: false,
    onMove: () => undefined,
  };
  return (
    <>
      <Popover
        label={t('catalog.boardMenu', { name: board.name })}
        width={240}
        alignEnd
        trigger={(props) => (
          <button
            type="button"
            {...props}
            className={styles.tileMenu}
            aria-label={t('catalog.boardMenu', { name: board.name })}
          >
            <Icons.more />
          </button>
        )}
      >
        {(close) => (
          <MenuList>
            {order && (
              <MoveMenuItems
                upLabel={t('catalog.moveUp', { name: board.name })}
                downLabel={t('catalog.moveDown', { name: board.name })}
                canMoveUp={previous !== undefined}
                canMoveDown={next !== undefined}
                disabled={pending}
                onUp={() => {
                  close();
                  if (previous) onMove({ before_id: previous.id });
                }}
                onDown={() => {
                  close();
                  if (next) onMove({ after_id: next.id });
                }}
              />
            )}
            {board.can_edit_details && (
              <MenuRow>
                <MenuItem
                  icon={<Icons.palette />}
                  onClick={() => {
                    close();
                    setStyling(true);
                  }}
                >
                  {t('home.menuAppearance')}
                </MenuItem>
              </MenuRow>
            )}
            {board.can_edit_details && inTeam && (
              <MenuRow>
                <MenuItem
                  icon={<Icons.tag />}
                  onClick={() => {
                    close();
                    setLabelling(true);
                  }}
                >
                  {t('home.menuEditLabels')}
                </MenuItem>
              </MenuRow>
            )}
            {onMoveToSection && (
              <MenuRow>
                <MenuItem
                  icon={<Icons.folder />}
                  onClick={() => {
                    close();
                    onMoveToSection();
                  }}
                >
                  {t('sections.moveTo')}
                </MenuItem>
              </MenuRow>
            )}
          </MenuList>
        )}
      </Popover>
      {styling && (
        <BoardCardAppearanceDialog
          board={board}
          onClose={() => {
            setStyling(false);
          }}
        />
      )}
      {labelling && board.workspace_id && (
        <BoardLabelsDialog
          board={{
            id: board.id,
            name: board.name,
            workspace_id: board.workspace_id,
            labels: board.labels ?? [],
          }}
          onClose={() => {
            setLabelling(false);
          }}
        />
      )}
    </>
  );
}

/** One board tile; its leading icon (board) tells it from a database. */
export function BoardTile({
  board,
  order,
  location,
  onMoveToSection,
}: {
  board: Board;
  order?: TileOrder | undefined;
  /** Where it is filed ("Development / Backend"), shown in search results (UD-STRUCT). */
  location?: string | undefined;
  /** Opens the "Move to section" picker; offered to whoever may edit the board. */
  onMoveToSection?: (() => void) | undefined;
}) {
  const { t } = useTranslation();
  const here = useLocation();
  const prefetchBoard = usePrefetchBoard();
  const ref = useRef<HTMLLIElement>(null);
  const [dragging, setDragging] = useState(false);
  const [edge, setEdge] = useState<Edge | null>(null);
  const workspaceId = board.workspace_id;
  const onDropBeside = order?.onDropBeside;
  const previousId = order?.previous?.id;
  const nextId = order?.next?.id;
  const dragEnabled = onDropBeside !== undefined && workspaceId !== null;

  useEffect(() => {
    const element = ref.current;
    if (!element || !dragEnabled) return;
    return combine(
      draggable({
        element,
        getInitialData: () => boardTileData(workspaceId, board.id),
        onDragStart: () => {
          setDragging(true);
        },
        onDrop: () => {
          setDragging(false);
        },
      }),
      dropTargetForElements({
        element,
        canDrop: ({ source }) =>
          isBoardTile(source.data) &&
          source.data.workspaceId === workspaceId &&
          source.data.boardId !== board.id,
        getData: ({ input }) =>
          attachClosestEdge(boardTileData(workspaceId, board.id), {
            element,
            input,
            allowedEdges: ['left', 'right'],
          }),
        onDrag: ({ self }) => {
          setEdge(extractClosestEdge(self.data));
        },
        onDragLeave: () => {
          setEdge(null);
        },
        onDrop: ({ self, source }) => {
          setEdge(null);
          if (!isBoardTile(source.data)) return;
          const side = extractClosestEdge(self.data);
          const sourceId = source.data.boardId;
          // Dropped where it already is: nothing to save.
          if (side === 'left' && previousId !== sourceId) {
            onDropBeside(sourceId, { before_id: board.id });
          }
          if (side === 'right' && nextId !== sourceId) {
            onDropBeside(sourceId, { after_id: board.id });
          }
        },
      }),
    );
  }, [board.id, workspaceId, dragEnabled, onDropBeside, previousId, nextId]);

  return (
    <li
      ref={ref}
      className={[
        styles.tile,
        lookSurface,
        dragEnabled && styles.tileDraggable,
        dragging && styles.tileDragging,
        edge && styles[`tileDrop-${edge}`],
      ]
        .filter(Boolean)
        .join(' ')}
      data-testid="board-tile"
      {...lookProps(board.appearance?.tile)}
      style={
        {
          ...lookProps(board.appearance?.tile).style,
          '--board-color': boardColor(board),
        } as CSSProperties
      }
    >
      <Link
        to={`/app/boards/${board.id}`}
        state={originStateFrom(here)}
        className={styles.tileLink}
        // A link drags as a link; the whole tile is the handle instead.
        draggable={dragEnabled ? false : undefined}
        onPointerEnter={() => void prefetchBoard(board.id)}
        onFocus={() => void prefetchBoard(board.id)}
      >
        <span className={styles.tileColor} aria-hidden />
        <LabelStrip labels={board.labels ?? []} />
        <span className={styles.tileName}>
          <KindIcon icon={<Icons.board />} label={t('workspace.objectTypes.board')} />
          <span className={styles.tileTitle} title={board.name}>
            {board.name}
          </span>
        </span>
        {location && (
          <span className={styles.tileLocation} data-testid="resource-location">
            <Icons.folder /> {location}
          </span>
        )}
        {board.description && <span className={styles.tileDescription}>{board.description}</span>}
        {board.owner_name && (
          <ResourceMeta>
            <MetaItem icon={<Icons.crown />}>
              {t('boards.ownerName', { name: board.owner_name })}
            </MetaItem>
          </ResourceMeta>
        )}
      </Link>
      <CardActions>
        <FavoriteButton board={board} />
        {(order || onMoveToSection || board.can_edit_details) && (
          <TileMenu board={board} order={order} onMoveToSection={onMoveToSection} />
        )}
      </CardActions>
    </li>
  );
}

function BoardGrid({ boards, labelledBy }: { boards: Board[]; labelledBy?: string }) {
  return (
    <ul className={styles.grid} aria-labelledby={labelledBy}>
      {boards.map((board) => (
        <BoardTile key={board.id} board={board} />
      ))}
    </ul>
  );
}

/**
 * Boards of a workspace. Favorites (per-user state) get their own block on top and are
 * not repeated below; without favorites there is a single plain list.
 */
export function BoardList({ boards }: { boards: Board[] }) {
  const { t } = useTranslation();
  if (boards.length === 0) return <EmptyState title={t('boards.empty')} />;
  const { favorites, others } = splitFavorites(boards);
  if (favorites.length === 0) return <BoardGrid boards={others} />;
  return (
    <div className={styles.sections}>
      <section aria-labelledby="boards-favorites" data-testid="favorite-boards">
        <h3 id="boards-favorites" className={styles.sectionTitle}>
          <span aria-hidden>★ </span>
          {t('boards.favorites')}
        </h3>
        <BoardGrid boards={favorites} />
      </section>
      {others.length > 0 && (
        <>
          <hr className={styles.divider} />
          <section aria-labelledby="boards-all" data-testid="all-boards">
            <h3 id="boards-all" className={styles.sectionTitle}>
              {t('boards.all')}
            </h3>
            <BoardGrid boards={others} />
          </section>
        </>
      )}
    </div>
  );
}
